import axios, { AxiosRequestConfig } from "axios";

import { API_TIMEOUT_MS, getApiToken, invalidateApiToken } from "./apiToken";

const isServer = typeof window === "undefined";

export const api = axios.create({
  baseURL: isServer ? `${process.env.NEXT_API_LAMBDA_URL}/` : "/api/ss/",
  timeout: API_TIMEOUT_MS
});

type RetriableConfig = AxiosRequestConfig & { _retriedAuth?: boolean };

api.interceptors.request.use(async (config) => {
  if (isServer) {
    const token = await getApiToken();
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(undefined, async (error) => {
  const config: RetriableConfig | undefined = error?.config;

  if (isServer && error?.response?.status === 401 && config && !config._retriedAuth) {
    config._retriedAuth = true;
    const rejected = String(config.headers?.Authorization ?? "").replace(/^Bearer\s+/, "");
    invalidateApiToken(rejected);
    return api.request(config);
  }

  return Promise.reject(error);
});

const NAME_LIST_ENDPOINTS: { pattern: RegExp; toName: (item: any) => string }[] = [
  { pattern: /Vehicles\/GetBrands$/i, toName: (item) => item?.brand },
  { pattern: /Vehicles\/GetVehiclesByBrand$/i, toName: (item) => item?.model }
];

function toUniqueNames(list: unknown[], toName: (item: any) => string): string[] {
  const names = list
    .map((item) => (typeof item === "string" ? item : toName(item)))
    .filter((name): name is string => Boolean(name));

  return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, "pt-BR"));
}

function attachEnvelope(list: unknown[], envelope: { status: unknown; msgs: unknown }) {
  const fields = { status: envelope.status, msgs: envelope.msgs, result: list, value: list };

  Object.entries(fields).forEach(([key, value]) => {
    Object.defineProperty(list, key, { value, enumerable: false });
  });

  return list;
}

api.interceptors.response.use((response) => {
  const data = response?.data;

  const isEnvelope =
    data &&
    typeof data === "object" &&
    !Array.isArray(data) &&
    "status" in data &&
    "msgs" in data &&
    "result" in data;

  if (isEnvelope && Array.isArray(data.result)) {
    const url = (response.config?.url ?? "").split("?")[0];
    const nameList = NAME_LIST_ENDPOINTS.find(({ pattern }) => pattern.test(url));

    const list = nameList ? toUniqueNames(data.result, nameList.toName) : data.result;

    response.data = attachEnvelope(list, data);
  }

  return response;
});
